/**
 * Public home page
 * PURPOSE: Renders navigation, exclusive offers, filters, why-us, testimonials and footer.
 *
 * EDITING GUIDE:
 * - Read the comments above each section or function before changing behavior.
 * - Keep validation, authentication and error handling in place for production safety.
 * - Search for "EDIT HERE" to find the most common business-content settings.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { api, assetUrl } from '../api';
import LanguageSwitcher from '../components/LanguageSwitcher';
import WhatsAppIcon from '../components/WhatsAppIcon';
import { buildGeneralWhatsAppUrl, buildPropertyWhatsAppUrl } from '../utils/whatsapp';
import { useLanguage } from '../i18n';
import './Home.css';

// FILTER DEFAULTS: both the visible form and applied search begin with these values.
// PAGINATION: each public homepage page shows exactly 25 property cards.
const PROPERTIES_PER_PAGE = 25;

const EMPTY_FILTERS = {
  category: '',
  type: '',
  location: '',
  minPrice: '',
  maxPrice: '',
  bedrooms: '',
  sort: 'newest',
};

// EDIT HERE: replace these URLs with the agency's real social-media pages.
const SOCIAL_LINKS = [
  { name: 'Facebook', url: 'https://www.facebook.com/capitalimmoplus/', icon: 'facebook' },
  { name: 'Instagram', url: 'https://www.instagram.com/capitalimmoplus/', icon: 'instagram' },
  { name: 'TikTok', url: 'https://www.tiktok.com/@agencecapitalimmo6', icon: 'tiktok' },
];

/**
 * Render the small inline SVG used for a footer social network.
 */
function SocialIcon({ name }) {
  if (name === 'facebook') {
    return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M14 8h3V4h-3c-3.3 0-5 2-5 5v2H6v4h3v9h4v-9h3.2l.8-4H13V9c0-.7.3-1 1-1Z" /></svg>;
  }
  if (name === 'instagram') {
    return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5Zm0 2a3 3 0 0 0-3 3v10a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3V7a3 3 0 0 0-3-3H7Zm10.5 1.5a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5ZM12 7a5 5 0 1 1 0 10 5 5 0 0 1 0-10Zm0 2a3 3 0 1 0 0 6 3 3 0 0 0 0-6Z" /></svg>;
  }
  return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15.5 3c.4 2.4 1.8 3.9 4.5 4.1v3.2a8.7 8.7 0 0 1-4.4-1.2v6.1a6.2 6.2 0 1 1-5.4-6.1v3.3a3 3 0 1 0 2.1 2.8V3h3.2Z" /></svg>;
}

/**
 * Render the average property rating and total review count on listing cards.
 */
function StarSummary({ stats }) {
  const average = Number(stats?.average || 0);
  const count = Number(stats?.count || 0);
  return (
    <span className="card-rating" aria-label={`${average} / 5`}>
      <span>★</span> {average ? average.toFixed(1) : '—'} <small>({count})</small>
    </span>
  );
}

/**
 * Render the complete public homepage and manage filtering, navigation and scroll behavior.
 */
const Home = () => {
  // TRANSLATION: t(key) returns text in the active FR/EN/AR language.
  const { t, language } = useLanguage();

  // COMPONENT STATE: property data, filter drafts, navigation state and scroll controls.
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [scrolled, setScrolled] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  // PAGINATION STATE: the current public listing page starts at page 1.
  const [currentPage, setCurrentPage] = useState(1);

  // INITIAL LOAD: start animations, load approved properties and watch page scrolling.
  useEffect(() => {
    AOS.init({ duration: 700, once: true, offset: 80 });
    api.get('/api/properties')
      .then((response) => setProperties(response.data.data || []))
      .catch((error) => console.error('Property loading error:', error))
      .finally(() => setLoading(false));

    /**
     * Encapsulate the on scroll behavior used by this file.
     */
    const onScroll = () => {
      setScrolled(window.scrollY > 30);
      setShowScrollTop(window.scrollY > 500);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // DERIVED LIST: filter and sort in memory whenever properties or applied filters change.
  const filteredProperties = useMemo(() => {
    const location = appliedFilters.location.trim().toLowerCase();
    let result = properties.filter((property) => {
      const matchCategory = !appliedFilters.category || property.category === appliedFilters.category;
      const matchType = !appliedFilters.type || property.type === appliedFilters.type;
      const matchLocation = !location || String(property.location || '').toLowerCase().includes(location);
      const matchMin = !appliedFilters.minPrice || Number(property.price) >= Number(appliedFilters.minPrice);
      const matchMax = !appliedFilters.maxPrice || Number(property.price) <= Number(appliedFilters.maxPrice);
      const matchBedrooms = !appliedFilters.bedrooms || Number(property.bedrooms || 0) >= Number(appliedFilters.bedrooms);
      return matchCategory && matchType && matchLocation && matchMin && matchMax && matchBedrooms;
    });

    result = [...result].sort((a, b) => {
      if (appliedFilters.sort === 'priceAsc') return Number(a.price) - Number(b.price);
      if (appliedFilters.sort === 'priceDesc') return Number(b.price) - Number(a.price);
      if (appliedFilters.sort === 'rating') return Number(b.reviewStats?.average || 0) - Number(a.reviewStats?.average || 0);
      return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
    });
    return result;
  }, [appliedFilters, properties]);

  // PAGINATED LIST: slice the filtered results into groups of 25 listings.
  const totalPages = Math.max(1, Math.ceil(filteredProperties.length / PROPERTIES_PER_PAGE));
  const paginatedProperties = useMemo(() => {
    const firstIndex = (currentPage - 1) * PROPERTIES_PER_PAGE;
    return filteredProperties.slice(firstIndex, firstIndex + PROPERTIES_PER_PAGE);
  }, [currentPage, filteredProperties]);

  // Keep the selected page valid when filtering removes enough listings to reduce the page count.
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  // Refresh scroll animations after React replaces the 25 visible cards with another page.
  useEffect(() => {
    AOS.refresh();
  }, [currentPage, paginatedProperties]);

  /**
   * Change the listing page and return the visitor to the start of the property section.
   */
  const goToPage = (pageNumber) => {
    const safePage = Math.min(Math.max(Number(pageNumber) || 1, 1), totalPages);
    setCurrentPage(safePage);
    setTimeout(() => document.getElementById('properties')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 40);
  };

  // Show the first, last and nearby page numbers without creating an excessively long control.
  const paginationItems = useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, index) => index + 1);
    const candidates = new Set([1, totalPages, currentPage - 1, currentPage, currentPage + 1]);
    const pages = [...candidates].filter((page) => page >= 1 && page <= totalPages).sort((a, b) => a - b);
    const items = [];
    pages.forEach((page, index) => {
      if (index > 0 && page - pages[index - 1] > 1) items.push(`ellipsis-${page}`);
      items.push(page);
    });
    return items;
  }, [currentPage, totalPages]);

  /**
   * Apply filters to the current data set.
   */
  const applyFilters = (event) => {
    event?.preventDefault();
    setAppliedFilters(draftFilters);
    setCurrentPage(1);
    setTimeout(() => document.getElementById('properties')?.scrollIntoView({ behavior: 'smooth' }), 40);
  };

  /**
   * Restore filters to its default values.
   */
  const resetFilters = () => {
    setDraftFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setCurrentPage(1);
  };

  /**
   * Scroll to a named homepage section and close the mobile navigation drawer.
   */
  const navTo = (id) => {
    setMenuOpen(false);
    setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }), 40);
  };

  // TESTIMONIAL CONTENT: translated quote text with a language-specific customer label.
  const testimonials = [
    { quote: t('testimonial1'), label: language === 'ar' ? 'حريف — بيع' : language === 'en' ? 'Client — Sale' : 'Client — Vente' },
    { quote: t('testimonial2'), label: language === 'ar' ? 'حريف — شراء' : language === 'en' ? 'Client — Purchase' : 'Client — Achat' },
    { quote: t('testimonial3'), label: language === 'ar' ? 'حريف — كراء' : language === 'en' ? 'Client — Rental' : 'Client — Location' },
  ];
  const logoUrl = '/logo.jpeg?v=2';

  return (
    <div className="home-container" id="home">
      {/* EDIT HERE: floating WhatsApp contact. The visible content is the logo, not text. */}
      <a
        href={buildGeneralWhatsAppUrl('21695999600')}
        className="whatsapp-float"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Contacter Capital Immobilière sur WhatsApp"
        title="WhatsApp"
      >
        <WhatsAppIcon size={31} />
      </a>

      {/* EDIT HERE: scroll-to-top button visibility and icon. */}
      <button
        type="button"
        className={`scroll-top-button ${showScrollTop ? 'visible' : ''}`}
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        aria-label="Scroll to top"
      >
        <span aria-hidden="true">↑</span>
      </button>

      {/* MAIN NAVIGATION: logo, desktop links, posting link and language switcher. */}
      <nav className={`navbar ${scrolled ? 'navbar-scrolled' : ''}`}>
        <Link to="/" className="nav-logo" aria-label="Capital Immobilière">
          <img src={logoUrl} alt="Capital Immobilière" className="logo-img" />
          <span className="logo-text">CAPITAL <span>IMMOBILIÈRE</span></span>
        </Link>

        <div className="nav-links">
          <button type="button" onClick={() => navTo('home')}>{t('navHome')}</button>
          <button type="button" onClick={() => navTo('properties')}>{t('navProperties')}</button>
          <button type="button" onClick={() => navTo('why-us')}>{t('navWhy')}</button>
          <button type="button" onClick={() => navTo('testimonials')}>{t('navTestimonials')}</button>
          <Link className="post-property-nav" to="/account?post=1">{t('postProperty')}</Link>
          <LanguageSwitcher compact />
        </div>

        <button className={`hamburger ${menuOpen ? 'open' : ''}`} onClick={() => setMenuOpen((open) => !open)} aria-label="Menu" type="button">
          <span /><span /><span />
        </button>
      </nav>

      {/* MOBILE NAVIGATION: shown only when the hamburger button is open. */}
      <div className={`mobile-drawer ${menuOpen ? 'drawer-open' : ''}`}>
        <button type="button" onClick={() => navTo('home')}>{t('navHome')}</button>
        <button type="button" onClick={() => navTo('properties')}>{t('navProperties')}</button>
        <button type="button" onClick={() => navTo('why-us')}>{t('navWhy')}</button>
        <button type="button" onClick={() => navTo('testimonials')}>{t('navTestimonials')}</button>
        <Link to="/account?post=1" onClick={() => setMenuOpen(false)}>{t('postProperty')}</Link>
        <LanguageSwitcher />
      </div>
      {menuOpen && <button className="drawer-backdrop" aria-label="Close menu" type="button" onClick={() => setMenuOpen(false)} />}

      {/* HERO: main introductory image, message and primary actions. */}
      <header className="hero">
        <div className="hero-overlay">
          <div className="hero-content" data-aos="fade-up">
            <span className="hero-eyebrow">CAPITAL IMMOBILIÈRE · TUNIS</span>
            <h1>{t('heroTitle')}</h1>
            <p>{t('heroSubtitle')}</p>
            <div className="hero-actions">
              <button type="button" onClick={() => navTo('properties')}>{t('navProperties')}</button>
              <Link to="/account?post=1">{t('postProperty')}</Link>
            </div>
          </div>
        </div>
      </header>

      {/* ABOUT: short agency introduction. Text comes from the translation file. */}
      <section id="about" className="about-section section-pad" data-aos="fade-up">
        <div className="section-heading"><span>CAPITAL IMMOBILIÈRE</span><h2>{t('aboutTitle')}</h2></div>
        <p>{t('aboutText')}</p>
      </section>

      {/* EDIT HERE: “Why us” content and the founding year. */}
      <section id="why-us" className="why-section section-pad">
        <div className="section-heading light" data-aos="fade-up"><span>{t('since')}</span><h2>{t('whyTitle')}</h2></div>
        <div className="why-grid">
          {[
            ['2006', t('whyExperience')],
            ['01', t('whySupport')],
            ['✓', t('whySelection')],
            ['24/7', t('whyVisibility')],
          ].map(([icon, text], index) => <article key={text} data-aos="fade-up" data-aos-delay={index * 70}><strong>{icon}</strong><p>{text}</p></article>)}
        </div>
      </section>

      {/* EXCLUSIVE OFFERS: heading, advanced filters and public property cards. */}
      <main id="properties" className="listings-section section-pad">
        <div className="section-heading" data-aos="fade-up"><span>PORTFOLIO</span><h2>{t('listingsTitle')}</h2><p>{filteredProperties.length} {t('results')}</p></div>

        {/* EDIT HERE: the property filter is intentionally placed below “Nos offres exclusives”. */}
        <section className="filter-shell" aria-label="Property filters" data-aos="fade-up">
          <form className="advanced-filter" onSubmit={applyFilters}>
            <label><span>{t('filterCategory')}</span><select value={draftFilters.category} onChange={(e) => setDraftFilters({ ...draftFilters, category: e.target.value })}>
              <option value="">{t('all')}</option>
              {['Appartement', 'Villa', 'Maison', 'Terrain', 'Bureau', 'Local commercial', 'Autre'].map((item) => <option key={item} value={item}>{item}</option>)}
            </select></label>
            <label><span>{t('filterOperation')}</span><select value={draftFilters.type} onChange={(e) => setDraftFilters({ ...draftFilters, type: e.target.value })}>
              <option value="">{t('all')}</option><option value="Vente">{t('sale')}</option><option value="Location">{t('rent')}</option>
            </select></label>
            <label className="filter-location"><span>{t('filterLocation')}</span><input value={draftFilters.location} onChange={(e) => setDraftFilters({ ...draftFilters, location: e.target.value })} placeholder="La Marsa, Menzah..." /></label>
            <label><span>{t('filterMinPrice')}</span><input type="number" min="0" value={draftFilters.minPrice} onChange={(e) => setDraftFilters({ ...draftFilters, minPrice: e.target.value })} /></label>
            <label><span>{t('filterMaxPrice')}</span><input type="number" min="0" value={draftFilters.maxPrice} onChange={(e) => setDraftFilters({ ...draftFilters, maxPrice: e.target.value })} /></label>
            <label><span>{t('filterBedrooms')}</span><select value={draftFilters.bedrooms} onChange={(e) => setDraftFilters({ ...draftFilters, bedrooms: e.target.value })}>
              <option value="">{t('all')}</option>{[1, 2, 3, 4, 5].map((number) => <option key={number} value={number}>{number}+</option>)}
            </select></label>
            <label><span>{t('filterSort')}</span><select value={draftFilters.sort} onChange={(e) => setDraftFilters({ ...draftFilters, sort: e.target.value })}>
              <option value="newest">{t('newest')}</option><option value="priceAsc">{t('priceAsc')}</option><option value="priceDesc">{t('priceDesc')}</option><option value="rating">{t('rating')}</option>
            </select></label>
            <div className="filter-actions"><button type="submit">{t('filterSearch')}</button><button type="button" onClick={resetFilters}>{t('filterReset')}</button></div>
          </form>
        </section>
        {loading ? <div className="loader">{t('loading')}</div> : filteredProperties.length ? (
          <div className="property-grid">
            {paginatedProperties.map((property) => (
              <article key={property._id} className="property-card" data-aos="fade-up">
                <div className="img-container">
                  <img src={assetUrl(property.images?.[0] || property.image)} alt={property.title} onError={(e) => { e.currentTarget.src = '/placeholder.svg'; }} />
                  <span className="type-badge">{property.type}</span>
                  {property.status !== 'disponible' && <span className={`card-status-badge ${property.status}`}>{property.status}</span>}
                  <span className="price-tag">{Number(property.price).toLocaleString('fr-FR')} DT</span>
                </div>
                <div className="card-info">
                  <div className="card-topline"><span>{property.category || 'Immobilier'}</span><StarSummary stats={property.reviewStats} /></div>
                  <h3>{property.title}</h3>
                  <p className="location">📍 {property.location}</p>
                  <div className="property-specs">
                    {Number(property.bedrooms) > 0 && <span>🛏 {property.bedrooms} {t('bedrooms')}</span>}
                    {Number(property.bathrooms) > 0 && <span>🚿 {property.bathrooms} {t('bathrooms')}</span>}
                    {Number(property.area) > 0 && <span>↔ {property.area} {t('area')}</span>}
                  </div>
                  <div className="card-actions">
                    <Link to={`/property/${property._id}`}>{t('details')}</Link>
                    {/* This message contains the property title, location, price and direct page link. */}
                    <a href={buildPropertyWhatsAppUrl('21695999600', property)} target="_blank" rel="noopener noreferrer">WhatsApp</a>
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : <div className="no-results"><h3>{t('noResults')}</h3><button type="button" onClick={resetFilters}>{t('filterReset')}</button></div>}

        {/* PAGINATION: 25 property cards per page, with compact first/nearby/last page buttons. */}
        {!loading && filteredProperties.length > PROPERTIES_PER_PAGE && (
          <nav className="property-pagination" aria-label={t('paginationLabel')}>
            <button type="button" onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}>
              ← {t('paginationPrevious')}
            </button>
            <div className="pagination-pages">
              {paginationItems.map((item) => typeof item === 'number' ? (
                <button
                  type="button"
                  key={item}
                  className={item === currentPage ? 'active' : ''}
                  onClick={() => goToPage(item)}
                  aria-current={item === currentPage ? 'page' : undefined}
                  aria-label={`${t('paginationPage')} ${item}`}
                >
                  {item}
                </button>
              ) : <span key={item} aria-hidden="true">…</span>)}
            </div>
            <button type="button" onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}>
              {t('paginationNext')} →
            </button>
            <span className="pagination-summary">{t('paginationPage')} {currentPage} {t('paginationOf')} {totalPages}</span>
          </nav>
        )}
      </main>

      {/* EDIT HERE: testimonial cards can be replaced with real customer quotes later. */}
      <section id="testimonials" className="testimonials-section section-pad">
        <div className="section-heading light" data-aos="fade-up"><span>5 ★</span><h2>{t('testimonialsTitle')}</h2></div>
        <div className="testimonial-grid">
          {testimonials.map((item, index) => <blockquote key={item.label} data-aos="fade-up" data-aos-delay={index * 80}><div className="quote-stars">★★★★★</div><p>“{item.quote}”</p><footer>{item.label}</footer></blockquote>)}
        </div>
      </section>

      {/* OWNER CALL TO ACTION: sends property owners to account creation/posting. */}
      <section className="owner-cta section-pad" data-aos="fade-up">
        <div><span>{t('since')}</span><h2>{t('postProperty')}</h2><p>{t('accountIntro')}</p></div>
        <Link to="/account?post=1">{t('postProperty')} →</Link>
      </section>

      {/* FOOTER: branches, contact details, social links and language switcher. */}
      <footer className="footer" id="footer">
        <div className="footer-brand"><img src={logoUrl} alt="Capital Immobilière" /><div><h2>CAPITAL IMMOBILIÈRE</h2><p>{t('since')}</p></div></div>
        <div className="footer-grid">
          <div><h3>Menzah 9</h3><p>Rue 7301, 52 Menzah 9B, Tunis</p><a href="tel:+21695999600">+216 95 999 600</a><a href="tel:+21653330540">+216 53 330 540</a></div>
          <div><h3>Ain Zaghouan</h3><p>Avenue Khaled Ibn Walid, Cité Les Palmiers, Tunis</p><a href="tel:+21695108000">+216 95 108 000</a><a href="tel:+21623333647">+216 23 333 647</a></div>
          <div><h3>{t('contactTitle')}</h3><a href="mailto:capitalimmo432@gmail.com">capitalimmo432@gmail.com</a><Link to="/account?post=1">{t('postProperty')}</Link></div>
          <div><h3>{t('followUs')}</h3><div className="social-links">{SOCIAL_LINKS.map((social) => <a key={social.name} href={social.url} target="_blank" rel="noopener noreferrer" aria-label={social.name}><SocialIcon name={social.icon} /></a>)}</div></div>
        </div>
        <div className="footer-bottom"><p>© 2026 CAPITAL IMMOBILIÈRE — {t('rights')}</p><LanguageSwitcher /></div>
      </footer>
    </div>
  );
};

export default Home;
